import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconKey,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUser,
  IconUsersGroup,
} from "@/lib/icons";
import { Pencil } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import {
  type UserRead,
  deactivateUser,
  listUsers,
  resetUserPassword,
} from "@/lib/api/users";
import { useAuthStore } from "@/stores/auth";

import { UserEditDialog } from "./UserEditDialog";
import { UserFormDialog } from "./UserFormDialog";

export function UserListPage() {
  const { t } = useTranslation();
  const currentUser = useAuthStore((s) => s.user);
  const canCreate = useAuthStore((s) => s.hasPermission("user.create"));
  const canUpdate = useAuthStore((s) => s.hasPermission("user.update"));
  const canDelete = useAuthStore((s) => s.hasPermission("user.delete"));
  const queryClient = useQueryClient();

  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, 300);
  const [dialogOpen, { open: openDialog, close: closeDialog }] = useDisclosure(false);
  const [editing, setEditing] = useState<UserRead | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["users", debouncedQuery],
    queryFn: () => listUsers({ q: debouncedQuery || undefined, limit: 100 }),
  });

  const resetMutation = useMutation({
    mutationFn: resetUserPassword,
    onSuccess: (response) => {
      notifications.show({
        color: "blue",
        title: t("users.temp_password_title"),
        message: response.temporary_password,
        autoClose: false,
      });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivateUser,
    onSuccess: () => {
      notifications.show({ color: "green", message: t("users.deactivated") });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("users.title")}
        subtitle={data ? `${data.total} ${t("users.fields.full_name")}` : undefined}
        actions={
          canCreate && (
            <Button leftSection={<IconPlus size={16} strokeWidth={1.8} />} onClick={openDialog}>
              {t("users.create")}
            </Button>
          )
        }
      />

      <Card padding="md">
        <TextInput
          rightSection={<IconSearch size={18} strokeWidth={1.8} />}
          placeholder={t("users.search_placeholder")}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0}>
        <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover verticalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("users.fields.full_name")}</Table.Th>
                <Table.Th>{t("users.fields.login")}</Table.Th>
                <Table.Th>{t("users.fields.role")}</Table.Th>
                <Table.Th>{t("users.fields.specialty")}</Table.Th>
                <Table.Th>{t("users.fields.status")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.length === 0 && !isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <EmptyState
                      icon={<IconUsersGroup size={32} strokeWidth={1.5} />}
                      title={t("users.empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.map((u) => (
                <Table.Tr key={u.id}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar
                        size={36}
                        radius="md"
                        variant="gradient"
                        gradient={{ from: "brand.5", to: "brand.8", deg: 135 }}
                      >
                        <IconUser size={18} strokeWidth={1.6} />
                      </Avatar>
                      <Text fw={600} size="sm">
                        {u.full_name}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {u.email}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="blue" radius="sm">
                      {t(`users.roles.${u.role}`)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{u.specialty ? t(`users.specialties.${u.specialty}`) : "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge
                      status={u.is_active ? "active" : "inactive"}
                      i18nPrefix="users"
                    />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {canUpdate && (
                        <Tooltip label={t("common.edit")}>
                          <ActionIcon
                            variant="subtle"
                            color="brand"
                            onClick={() => setEditing(u)}
                          >
                            <Pencil size={16} strokeWidth={1.8} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {canUpdate && (
                        <Tooltip label={t("users.reset_password")}>
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            onClick={() => resetMutation.mutate(u.id)}
                            loading={
                              resetMutation.isPending && resetMutation.variables === u.id
                            }
                          >
                            <IconKey size={16} strokeWidth={1.8} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {canDelete && u.is_active && u.id !== currentUser?.id && (
                        <Tooltip label={t("users.deactivate")}>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => deactivateMutation.mutate(u.id)}
                            loading={
                              deactivateMutation.isPending &&
                              deactivateMutation.variables === u.id
                            }
                          >
                            <IconTrash size={16} strokeWidth={1.8} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <UserFormDialog opened={dialogOpen} onClose={closeDialog} />
      <UserEditDialog
        opened={!!editing}
        onClose={() => setEditing(null)}
        user={editing}
      />
    </Stack>
  );
}
